/*
 * ELTANE — product description accordions
 *
 * The product description is one long admin-authored block: a short lead
 * paragraph, then six or seven headed sections (Flavour Profile, Why You'll
 * Love It, When to Use, The Science, Ingredients…). Rendered flat, that is
 * several screens of prose sitting between the buy buttons and everything
 * below them, and nobody reads it — the shopper who wants the ingredient list
 * has to scroll past four sections that do not concern them.
 *
 * So group it the way the rest of the product page already presents detail:
 * one collapsible row per heading, all closed, opened on demand.
 *
 * Done here rather than in Liquid on purpose. The copy lives in the product's
 * description field in admin, so there is nothing to split on server-side
 * without string-surgery on rich text — and doing it in the DOM means every
 * product gets this automatically, with no per-product theme-editor work and
 * no change to how the copy is written.
 *
 * Progressive enhancement: if this script never runs, the description renders
 * exactly as it does today. Nothing is hidden by CSS ahead of the transform.
 *
 * Markup produced mirrors the `collapsible_tab` block in main-product.liquid
 * one-for-one, so component-accordion.css and eltane-product.css style these
 * with no additions.
 */
(function () {
  'use strict';

  var CONTAINER = '.product__description';
  var DONE = 'data-eltane-description-accordion';

  /* Dawn inlines this through `icon-caret.svg | inline_asset_content`, which is
     a Liquid-only filter — same path, same viewBox, so the existing
     `.summary__title + .icon-caret` rules apply unchanged. */
  var CARET =
    '<svg class="icon icon-caret" viewBox="0 0 10 6" aria-hidden="true" focusable="false">' +
    '<path fill="currentColor" fill-rule="evenodd" d="M9.354.646a.5.5 0 0 0-.708 0L5 4.293 1.354.646a.5.5 0 0 0-.708.708l4 4a.5.5 0 0 0 .708 0l4-4a.5.5 0 0 0 0-.708" clip-rule="evenodd"/>' +
    '</svg>';

  /*
   * Every one of these opens its own row, regardless of level.
   *
   * Splitting on the shallowest level only was the obvious reading of the
   * markup, but it gave the wrong shape: the description's h2s are two broad
   * pitches ("Pure Fuel. Nothing Else.") and its h3s are the parts a shopper
   * actually goes looking for — Flavour Profile, When to Use, Ingredients.
   * Nested, those were buried inside a row nobody would think to open to find
   * them, which is the opposite of what an FAQ list is for.
   *
   * Flat, every heading is one question. h5/h6 stay inline as sub-structure —
   * anything nested that deep is detail within an answer, not an answer.
   */
  var HEADING_TAGS = ['H2', 'H3', 'H4'];

  /*
   * Rich text pasted from a document often arrives as bold paragraphs rather
   * than as real headings, and the shopper cannot tell the difference. Treat a
   * paragraph whose entire content is one <strong>/<b> as the heading it is
   * visually pretending to be — otherwise a description written that way would
   * come out as a single accordion holding everything.
   */
  function isFauxHeading(node) {
    if (node.tagName !== 'P') return false;
    var children = node.children;
    if (children.length !== 1) return false;
    var only = children[0];
    if (only.tagName !== 'STRONG' && only.tagName !== 'B') return false;
    /* Any text outside the bold run means it is a sentence with emphasis, not a
       heading. Compare trimmed, since rich text is full of stray whitespace. */
    return node.textContent.trim() === only.textContent.trim();
  }

  /*
   * Which nodes start a row. Document order is preserved, so the rows come out
   * in the order the copy was written.
   *
   * Real headings win outright. Faux headings are considered only when there
   * are none, so a description that mixes both is not cut at every bold line.
   */
  function pickBreaks(children) {
    var found = children.filter(function (node) {
      return HEADING_TAGS.indexOf(node.tagName) !== -1;
    });

    if (found.length) return found;

    return children.filter(isFauxHeading);
  }

  /* Whitespace between blocks is a node but not content. Without this a
     description that opens on a heading gets an empty lead div, and a heading
     followed only by markup whitespace opens onto an empty panel. */
  function hasContent(list) {
    return list.some(function (node) {
      return node.nodeType === 1 || String(node.textContent || '').trim() !== '';
    });
  }

  function buildAccordion(heading, body, index) {
    var wrapper = document.createElement('div');
    wrapper.className = 'product__accordion accordion quick-add-hidden';

    var details = document.createElement('details');
    details.id = 'EltaneDescriptionAccordion-' + index;

    var summary = document.createElement('summary');

    var titleWrap = document.createElement('div');
    titleWrap.className = 'summary__title';

    /* The heading element itself is moved, not copied into a new one: that
       keeps its tag (an h2 in the description stays an h2, so the outline
       survives), keeps any classes the rich text put on it, and — the part a
       copy got wrong — takes it out of the container instead of leaving an
       emptied shell behind. `h4` is Dawn's type class, not a level, and is what
       the other accordion titles on this page carry. */
    heading.classList.add('h4', 'accordion__title');
    titleWrap.appendChild(heading);
    summary.appendChild(titleWrap);
    summary.insertAdjacentHTML('beforeend', CARET);

    var content = document.createElement('div');
    content.className = 'accordion__content rte';
    content.id = 'EltaneDescriptionContent-' + index;
    body.forEach(function (node) {
      content.appendChild(node);
    });

    details.appendChild(summary);
    details.appendChild(content);
    wrapper.appendChild(details);

    return wrapper;
  }

  function transform(container) {
    if (!container || container.hasAttribute(DONE)) return;

    var children = Array.prototype.slice.call(container.children);
    var breaks = pickBreaks(children);
    /* childNodes, not children: rich text carries whitespace between blocks,
       and moving only elements would strand those text nodes at the top of the
       container above the rebuilt content. They travel with their section. */
    var nodes = Array.prototype.slice.call(container.childNodes);

    /* One section is not a FAQ, it is a heading with a hide button. Below two,
       leave the description exactly as it was. */
    if (breaks.length < 2) return;

    /* Marked before the DOM work, not after: the theme editor can fire two
       section events in a row, and a half-built second pass would interleave
       the groups. */
    container.setAttribute(DONE, 'true');

    /* Everything ahead of the first heading is the lead paragraph. It stays
       visible — it is the sales pitch, and collapsing it would leave the
       description reading as a bare list of labels. */
    var groups = [];
    var intro = [];
    var current = null;

    nodes.forEach(function (node) {
      if (breaks.indexOf(node) !== -1) {
        current = { heading: node, body: [] };
        groups.push(current);
        return;
      }
      if (current) current.body.push(node);
      else intro.push(node);
    });

    /* Built off-document so the page lays out once instead of per row. */
    var fragment = document.createDocumentFragment();

    if (hasContent(intro)) {
      var lead = document.createElement('div');
      lead.className = 'eltane-product__description-lead';
      intro.forEach(function (node) {
        lead.appendChild(node);
      });
      fragment.appendChild(lead);
    }

    groups.forEach(function (group, index) {
      /* A heading with nothing under it would open onto an empty panel — keep
         it as plain text instead. */
      if (!hasContent(group.body)) {
        fragment.appendChild(group.heading);
        return;
      }
      fragment.appendChild(buildAccordion(group.heading, group.body, index));
    });

    container.appendChild(fragment);
  }

  function run() {
    Array.prototype.slice.call(document.querySelectorAll(CONTAINER)).forEach(transform);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', run);
  } else {
    run();
  }

  /* The theme editor replaces the section's markup wholesale on every setting
     change, which brings back an untransformed description. */
  document.addEventListener('shopify:section:load', run);
})();
